import { createContext, useState, useEffect, useCallback, useContext, ReactNode } from 'react';
import Snackbar, { Props as SnackbarProps } from '@classify/react-core-components/Snackbar';
import Button from '@classify/react-core-components/Button';

const SNACKBAR_STORAGE_KEY = 'pendingSnackbars';

interface SnackbarMessage extends Omit<SnackbarProps, 'isOpen' | 'action'> {
  id: string;
  actionProps?: Record<string, string | number> & {
    children: string;
    href: string;
  };
}

type CreateSnackbarArgs = Omit<SnackbarMessage, 'id'>;

interface SnackbarContextType {
  create: (snackbar: CreateSnackbarArgs) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

interface SnackbarProviderProps {
  children: ReactNode;
}

export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const [snackbarQueue, setSnackbarQueue] = useState<SnackbarMessage[]>([]);

  const create = useCallback((snackbar: CreateSnackbarArgs) => {
    const newSnackbar: SnackbarMessage = { id: String(Date.now()), ...snackbar };
    setSnackbarQueue((prevQueue) => [...prevQueue, newSnackbar]);

    let storedSnackbars: SnackbarMessage[] = [];
    try {
      storedSnackbars = JSON.parse(localStorage.getItem(SNACKBAR_STORAGE_KEY) || '[]');
    } catch {
      storedSnackbars = [];
    }

    localStorage.setItem(SNACKBAR_STORAGE_KEY, JSON.stringify([...storedSnackbars, newSnackbar]));
  }, []);

  useEffect(() => {
    const savedSnackbars: SnackbarMessage[] = JSON.parse(
      localStorage.getItem(SNACKBAR_STORAGE_KEY) || '[]'
    );
    if (savedSnackbars.length > 0) {
      setSnackbarQueue(savedSnackbars);
    }
  }, []);

  const handleClose = (snackbarId: string) => {
    const updatedQueue = snackbarQueue.filter((snackbar) => snackbar.id !== snackbarId);
    setSnackbarQueue(updatedQueue);
    localStorage.setItem(SNACKBAR_STORAGE_KEY, JSON.stringify(updatedQueue));
  };

  return (
    <SnackbarContext value={{ create }}>
      {children}
      {snackbarQueue.length > 0 && (
        <Snackbar
          key={snackbarQueue[0].id}
          isOpen
          onClose={(event, reason) => {
            snackbarQueue[0].onClose?.(event, reason);
            handleClose(snackbarQueue[0].id);
          }}
          type={snackbarQueue[0].type}
          message={snackbarQueue[0].message}
          action={
            snackbarQueue[0].actionProps && (
              <Button
                isOnContrasted
                variant="tertiary"
                component="a"
                {...snackbarQueue[0].actionProps}
              />
            )
          }
        />
      )}
    </SnackbarContext>
  );
};
